import React, { useState, useEffect, useRef, useMemo } from "react";
import Network from '../../../models/Network.js';

import * as vega from 'vega';
import * as vegaLite from 'vega-lite';
import * as vl from 'vega-lite-api';
import { Handler } from 'vega-tooltip';

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')

const brush = vl.selectInterval().encodings('x');
const click = vl.selectMulti().encodings('color');

const colorScale = {
    domain: ['Emergent', 'Refusal', 'Cancelled', 'Non-Emergent', 'Non-Acute', 'Unknown'],
    range: ['#e83838', '#FFCC00', '#222', '#1a6cc9', '#22b922', 'pink']
}



const config = {
    axis: {
        domain: false,
        tickColor: 'lightGray',
    },
    style: {
        'guide-label': {
            fontSize: 12,
            fill: '#000'
        },
        'guide-title': {
            fontSize: 18,
            fill: '#000'
        }
    },
    legend: {
        disable: true
    }
};

const pointSize = 500;

const GraphApiMultimodal = ({ data, graphParams, onIncidentSelect }) => {
    const containerRef = useRef(null);
    const [vegaSpec, setVegaSpec] = useState(null);

    // Helper function to convert date string to local time for Vega ingestion
    const datetimeConversion = (dateString) => {
        // if datestring is undefined or null, return null
        if (dateString === undefined || dateString === null) {
            return null;
        }

        // convert the date string to a valid ISO 8601 date string and add Z to the end to indicate UTC time
        let isoString = dateString.replace(' ', 'T');
        isoString = isoString + 'Z';

        // now convert the ISO string to a date object
        const date = new Date(isoString);

        // Format the date as YYYY-MM-DD HH:MM:SS
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    
        return formattedDate;
    };

    // Memoize the data processing to prevent unnecessary recalculations
    const processedData = useMemo(() => {
        return data.map(datum => ({
            ...datum,
            unit_back_in_service: datetimeConversion(datum.unit_back_in_service)
        }));
    }, [data]);

    // -------------------------------------------------------------------------------------

    const plot1 = vl.markPoint({ // defines the type of Mark being used
        // Mark properties
        filled: true,
        stroke: false,
        size: pointSize,
        opacity: 0.4
    })
        .data(processedData) // data set
        .encode(
            // vl.shape().fieldN('status').type('nominal'),
            vl.color().value('lightgray')
                .if(brush, vl.color().fieldN('report_type').scale(colorScale).title('Report Type')),
            vl.x().fieldT('unit_back_in_service')
                .timeUnit({ unit: 'monthdateyear', utc: false }) // Display month and date in local time
                .scale({padding: pointSize/40})
                .axis({title: 'Date', format: '%b the %dth'}),
            vl.y().fieldT('unit_back_in_service')
                .timeUnit({ unit: 'hoursminutes', utc: false }) // Display time of day in hours (local time)
                .scale({padding: pointSize/40})
                .axis({title: 'Unit Back in Service', format: '%I %p'}),
            vl.tooltip().fieldN('incident_number')
        )
        .width(700) // sized for normal, 1080p with no zoom
        .height(400)
        .select(brush)
        .transform(vl.filter(click));
    
    const plot2 = vl.markBar()
        .data(processedData) // data set
        .encode(
          vl.color().value('lightgray')
            .if(click, vl.color().fieldN('report_type').scale(colorScale).title('Report Type')),
          vl.x().count()
            .axis({title: 'Number of Responses'}),
          vl.y().fieldN('report_type').title('Type').scale({domain: colorScale.domain})
        )
        .width(700)
        .select(click)
        .transform(vl.filter(brush));

    // -------------------------------------------------------------------------------------

    useEffect(() => {
        const vegaLiteSpec = vl.vconcat(plot1, plot2)
            .config(config)
            .toSpec(); // compile to vega-lite spec
        setVegaSpec(vegaLite.compile(vegaLiteSpec).spec); // compile to vega spec
    }, [processedData]);

    useEffect(() => {
        if (vegaSpec) {
            const runtime = vega.parse(vegaSpec);
            const view = new vega.View(runtime)
                .renderer("svg") // imputSpec is null
                .initialize(containerRef.current)
                .hover() // Enable hover interaction
                .tooltip(new Handler().call); // Add Vega Tooltip handler
    
            view.addEventListener("click", (event, item) => {
                console.log("Point clicked");
                // console.log(item.datum.incident_number); // Access the clicked data point
                if (item.datum.incident_number !== undefined && item.datum.incident_number !== null) {
                    onIncidentSelect(item.datum.incident_number); // pass incident number to parent
                }
            });

            view.run();

            return () => {
                view.finalize();
            };
        }
    }, [vegaSpec]);

    return (
        <div>
            {/* Legend: + Read, o Unread */}
            <div ref={containerRef} />
        </div>
    );
};

export default GraphApiMultimodal;